import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { a as createLogger, e as emitEvent, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-radio';

const clRadioCss = ".sc-cl-radio-h{align-items:center;display:flex;flex-direction:row;flex-wrap:nowrap;justify-content:flex-start;position:relative;-webkit-user-select:none;-moz-user-select:none;user-select:none}.sc-cl-radio-h,.sc-cl-radio-h *.sc-cl-radio,.sc-cl-radio-h *.sc-cl-radio::before,.sc-cl-radio-h *.sc-cl-radio::after,.sc-cl-radio-h .sc-cl-radio-s>*,.sc-cl-radio-h .sc-cl-radio-s *,.sc-cl-radio-h .sc-cl-radio-s *::before,.sc-cl-radio-h .sc-cl-radio-s *::after{box-sizing:border-box}.sc-cl-radio-h .radio__container.sc-cl-radio,.sc-cl-radio-h .radio__input.sc-cl-radio{height:var(--spacing-3);min-height:var(--size-3);min-width:var(--size-3);position:relative;width:var(--spacing-3)}.sc-cl-radio-h .radio__input.sc-cl-radio{align-items:center;-webkit-appearance:none;-moz-appearance:none;appearance:none;border:var(--border-width) solid rgb(var(--color-brand-600));border-radius:var(--border-radius-circle);display:flex;justify-content:center;margin:0}.sc-cl-radio-h .radio__input.sc-cl-radio:hover:not(:checked):not(:disabled){border-color:rgba(var(--color-brand-600), var(--opacity-3))}.sc-cl-radio-h .radio__input.sc-cl-radio:focus-visible:not(:disabled){border-color:rgba(var(--system-color-focus));outline:none}.sc-cl-radio-h .radio__input.sc-cl-radio::before{background-color:rgb(var(--color-brand-main));border-radius:var(--border-radius-circle);content:\"\";height:60%;position:absolute;transform:scale(0);transform-origin:center;transition:var(--animation-duration-fast) transform var(--animation-timing-easeinout);width:60%}.sc-cl-radio-h .radio__input.sc-cl-radio:checked::before{transform:scale(1)}.sc-cl-radio-h .radio__label.sc-cl-radio{color:rgba(var(--color-neutral-800));cursor:default;position:relative;width:calc(100% - var(--spacing-1) - var(--size-3))}.sc-cl-radio-h .radio__label.sc-cl-radio *.sc-cl-radio{margin:0}.sc-cl-radio-h .radio__label.sc-cl-radio .microcopy.sc-cl-radio{top:var(--spacing-4)}.radio--disabled.sc-cl-radio-h .radio__input.sc-cl-radio{border-color:rgba(var(--color-neutral-900, 20, 20, 20), var(--opacity-3, 0.5));color:rgba(var(--color-neutral-800), var(--opacity-3, 0.5))}.radio--disabled.sc-cl-radio-h .radio__input.sc-cl-radio::before{background-color:rgba(var(--color-neutral-900, 20, 20, 20), var(--opacity-3, 0.5));border-color:transparent}.radio--disabled.sc-cl-radio-h .radio__label.sc-cl-radio{--typography-color:rgba(var(--color-neutral-900), var(--opacity-3))}.radio--disabled.sc-cl-radio-h .radio__input.sc-cl-radio{border-color:rgba(var(--color-neutral-300))}.radio--disabled.sc-cl-radio-h .radio__input.sc-cl-radio::before{background-color:rgba(var(--color-neutral-300))}.radio--disabled.sc-cl-radio-h *.sc-cl-radio{cursor:default}.radio--error.sc-cl-radio-h .radio__container.sc-cl-radio{border-color:rgba(var(--system-color-error-main))}.radio--error.sc-cl-radio-h .radio__checkmark.sc-cl-radio{background-color:rgba(var(--system-color-error-main))}.radio--error.sc-cl-radio-h .radio__input.sc-cl-radio{border-color:rgba(var(--system-color-error-main))}.radio--error.sc-cl-radio-h .radio__input.sc-cl-radio:hover:not(:checked):not(:disabled){border-color:rgba(var(--system-color-error-main))}.radio--error.sc-cl-radio-h .radio__input.sc-cl-radio::before{background-color:rgba(var(--system-color-error-main))}.sc-cl-radio-h:not(.radio--label-left) .radio__container.sc-cl-radio{order:0}.sc-cl-radio-h:not(.radio--label-left) .radio__label.sc-cl-radio{margin-left:var(--spacing-1);order:1}.radio--label-left.sc-cl-radio-h .radio__container.sc-cl-radio{order:1}.radio--label-left.sc-cl-radio-h .radio__label.sc-cl-radio{margin-right:var(--spacing-1);order:0}.radio--label-left.sc-cl-radio-h .radio__label.sc-cl-radio .microcopy.sc-cl-radio{right:0}";

const ClRadio$1 = /*@__PURE__*/ proxyCustomElement(class ClRadio extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clCheckedEv = createEvent(this, "clCheck", 7);
    this.clLabelClick = createEvent(this, "clLabelClick", 7);
    this.checkedEv = createEvent(this, "checkedEvent", 7);
    this.labelClick = createEvent(this, "labelClick", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Other checked radios with the same name as this radio are dechecked
     */
    this.decheckOtherRadios = () => {
      const checkedRadios = document.querySelectorAll(`cl-radio[name="${this.name}"][checked]`);
      checkedRadios.forEach(element => element.removeAttribute('checked'));
    };
    /**
     * Handle clicking on radio labels
     */
    this.handleLabelClick = () => {
      if (!this.disabled) {
        emitEvent(this, 'clLabelClick', Object.assign(Object.assign(Object.assign({ component, checked: this.checked, disabled: this.disabled, error: this.error }, (this.value ? { value: this.value } : {})), (this.name ? { name: this.name } : {})), (this.elementId ? { id: this.elementId } : {})), [this.name, this.elementId, this.value]);
        this.checked = true;
      }
    };
    /**
     * Handle clicking on radio buttons
     */
    this.handleRadioButtonClicked = () => {
      this.checked = true;
    };
    this.checked = false;
    this.disabled = false;
    this.error = false;
    this.elementId = '';
    this.labelLeft = false;
    this.label = '';
    this.name = '';
    this.value = '';
  }
  /**
   * Handles de-checking other radio elements when this radio is checked and emits the checked event
   */
  handleChecked() {
    if (this.checked) {
      this.decheckOtherRadios();
      emitEvent(this, 'clCheckedEv', Object.assign(Object.assign(Object.assign({ component, checked: this.checked, disabled: this.disabled, error: this.error }, (this.value ? { value: this.value } : {})), (this.name ? { name: this.name } : {})), (this.elementId ? { id: this.elementId } : {})), [this.elementId, this.name, this.value]);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'checkedEvent', 'clCheck');
    logDeprecatedEventOnce(this.logger, 'labelClick', 'clLabelClick');
  }
  render() {
    const { checked, disabled, error, label, labelLeft, name, value } = this;
    const showError = !disabled && error;
    return (h(Host, { class: cl('radio', {
        'radio--disabled': disabled,
        'radio--error': showError,
        'radio--label-left': labelLeft,
      }) }, h("div", { class: 'radio__container' }, h("input", { checked: checked, class: 'radio__input', disabled: disabled, name: name, type: 'radio', value: value, onChange: this.handleRadioButtonClicked })), h("div", { class: 'radio__label background-main' }, h("cl-typography", { class: 'label noWrap', onClick: this.handleLabelClick }, label))));
  }
  static get watchers() { return {
    "checked": ["handleChecked"]
  }; }
  static get style() { return clRadioCss; }
}, [2, "cl-radio", {
    "checked": [1540],
    "disabled": [4],
    "error": [516],
    "elementId": [1, "element-id"],
    "labelLeft": [4, "label-left"],
    "label": [1],
    "name": [513],
    "value": [1]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-radio", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-radio":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClRadio$1);
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClRadio = ClRadio$1;
const defineCustomElement = defineCustomElement$1;

export { ClRadio, defineCustomElement };

//# sourceMappingURL=cl-radio.js.map